import { z } from "zod"
import { Provider } from "./channels.js"
import { Id } from "./ids.js"

// ---- Graph definition ----

export const NodeKind = z.enum(["trigger", "condition", "public_reply", "private_message", "message", "wait", "handover", "tag", "end"])
export type NodeKind = z.infer<typeof NodeKind>

export const NODE_LABELS: Record<NodeKind, string> = {
  trigger: "Trigger",
  condition: "Condition",
  public_reply: "Public reply",
  private_message: "Private message",
  message: "Message",
  wait: "Wait",
  handover: "Handover",
  tag: "Tag contact",
  end: "End",
}

const KeywordMatch = z.object({ keywords: z.array(z.string().trim().min(1).max(60)).max(20), wholeWord: z.boolean().default(true), ignoreCase: z.boolean().default(true) })

export const TriggerConfig = z.object({
  kind: z.enum(["comment_keyword", "new_message", "manual"]),
  /** Stable key that survives edits; the entry key and the trigger rule row use it. */
  key: z.string().min(1).max(40).default("trigger"),
  match: KeywordMatch.default({ keywords: [], wholeWord: true, ignoreCase: true }),
  scope: z.enum(["all_posts", "selected_posts"]).default("all_posts"),
  postIds: z.array(z.string().min(1)).max(50).default([]),
  /** The connected account this trigger listens on; null means the automation's default account. */
  connectionId: Id.nullable().default(null),
})
export type TriggerConfig = z.infer<typeof TriggerConfig>

export const NodeConfig = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("trigger"), trigger: TriggerConfig }),
  z.object({ kind: z.literal("condition"), match: KeywordMatch, source: z.enum(["comment", "last_reply"]).default("comment") }),
  z.object({ kind: z.literal("public_reply"), text: z.string().max(1000) }),
  z.object({ kind: z.literal("private_message"), text: z.string().max(2000), button: z.object({ label: z.string().max(40), url: z.string().max(500) }).nullable().default(null) }),
  z.object({ kind: z.literal("message"), text: z.string().max(2000) }),
  z.object({ kind: z.literal("wait"), waitFor: z.literal("reply").default("reply"), timeoutMinutes: z.number().int().min(1).max(60 * 24 * 7).default(60 * 24) }),
  z.object({ kind: z.literal("handover"), reason: z.string().max(200).default("A journey handed the conversation to your team") }),
  z.object({ kind: z.literal("tag"), tagName: z.string().max(40) }),
  z.object({ kind: z.literal("end"), note: z.string().max(200).default("") }),
])
export type NodeConfig = z.infer<typeof NodeConfig>

export const FlowNode = z.object({ id: z.string().min(1).max(40), title: z.string().max(80), x: z.number(), y: z.number(), config: NodeConfig })
export type FlowNode = z.infer<typeof FlowNode>

export const EdgeLabel = z.enum(["next", "match", "no_match", "reply", "timeout"])
export const FlowEdge = z.object({ id: z.string().min(1).max(60), from: z.string(), to: z.string(), label: EdgeLabel.default("next") })
export type FlowEdge = z.infer<typeof FlowEdge>

export const FlowDefinition = z.object({
  schemaVersion: z.literal(1).default(1),
  nodes: z.array(FlowNode).min(1).max(40),
  edges: z.array(FlowEdge).max(80),
  entryScope: z.enum(["once_per_contact", "per_post", "always"]).default("once_per_contact"),
})
export type FlowDefinition = z.infer<typeof FlowDefinition>

// ---- Validation ----

export const ValidationIssue = z.object({
  code: z.string(),
  title: z.string(),
  detail: z.string(),
  location: z.string(),
  nodeId: z.string().nullable(),
  connectionId: Id.nullable(),
  severity: z.enum(["blocking", "recommendation"]),
})
export type ValidationIssue = z.infer<typeof ValidationIssue>

export const ValidationResult = z.object({
  ok: z.boolean(),
  issues: z.array(ValidationIssue),
  checks: z.array(z.object({ name: z.string(), passed: z.boolean() })),
})
export type ValidationResult = z.infer<typeof ValidationResult>

// ---- Simulation ----

export const SimulationScenario = z.enum(["match_reply", "no_match", "human_owned", "duplicate_entry", "match_timeout"])
export const SimulateInput = z.object({ scenario: SimulationScenario, commentText: z.string().max(500).default("GUIDE please!"), firstName: z.string().max(40).default("Maya"), replyText: z.string().max(500).default("I’d love some help choosing.") })
export type SimulateInput = z.infer<typeof SimulateInput>
export const SimulationStep = z.object({ nodeId: z.string().nullable(), title: z.string(), detail: z.string(), outcome: z.enum(["ok", "skipped", "stopped"]) })
export const SimulationResult = z.object({ passed: z.boolean(), trace: z.array(SimulationStep), messages: z.array(z.object({ kind: z.enum(["public_reply", "private_message", "message"]), text: z.string(), button: z.object({ label: z.string(), url: z.string() }).nullable() })), handedOver: z.boolean() })
export type SimulationResult = z.infer<typeof SimulationResult>

// ---- Automations ----

export const AutomationStatus = z.enum(["draft", "published", "paused"])
export const AutomationSummary = z.object({
  id: Id,
  name: z.string(),
  description: z.string(),
  provider: Provider.nullable(),
  connectionLabel: z.string().nullable(),
  status: AutomationStatus,
  /** Derived: a published automation whose connection lacks a required capability. */
  blocked: z.boolean(),
  liveVersion: z.number().int().nullable(),
  draftVersion: z.number().int().nullable(),
  runsLast7Days: z.number().int(),
  templateKey: z.string().nullable(),
  updatedAt: z.iso.datetime(),
})
export type AutomationSummary = z.infer<typeof AutomationSummary>

export const AutomationDetail = AutomationSummary.extend({
  connectionId: Id.nullable(),
  draft: z.object({ versionId: Id, number: z.number().int(), definition: FlowDefinition, validationPassedAt: z.iso.datetime().nullable(), simulationPassedAt: z.iso.datetime().nullable(), updatedAt: z.iso.datetime() }).nullable(),
  live: z.object({ versionId: Id, number: z.number().int(), definition: FlowDefinition, publishedAt: z.iso.datetime().nullable(), publishedByName: z.string().nullable() }).nullable(),
})
export type AutomationDetail = z.infer<typeof AutomationDetail>

export const AutomationsPage = z.object({
  automations: z.array(AutomationSummary),
  metrics: z.object({ published: z.number().int(), runsThisWeek: z.number().int(), handedOver: z.number().int(), needsAttention: z.number().int() }),
})
export type AutomationsPage = z.infer<typeof AutomationsPage>

export const CreateAutomationInput = z.object({
  name: z.string().trim().min(1).max(80),
  templateKey: z.string().max(40).optional(),
  connectionId: Id.optional(),
  /** Template setup fields (screen 17); ignored for a blank canvas. */
  setup: z.object({ keyword: z.string().trim().max(60).optional(), scope: z.enum(["all_posts", "selected_posts"]).optional(), postIds: z.array(z.string()).optional(), message: z.string().max(2000).optional(), buttonLabel: z.string().max(40).optional(), buttonUrl: z.string().max(500).optional() }).optional(),
})
export type CreateAutomationInput = z.infer<typeof CreateAutomationInput>

export const SaveDraftInput = z.object({ definition: FlowDefinition, name: z.string().trim().min(1).max(80).optional(), connectionId: Id.nullable().optional() })
/** Name and description belong to the automation, not to a version: changing them never opens a draft. */
export const UpdateAutomationInput = z.object({ name: z.string().trim().min(1).max(80).optional(), description: z.string().trim().max(200).optional() })
export type UpdateAutomationInput = z.infer<typeof UpdateAutomationInput>
export const PublishInput = z.object({ reviewed: z.literal(true), activate: z.boolean().default(true) })

export const Template = z.object({ key: z.string(), name: z.string(), category: z.enum(["Lead capture", "Support", "Custom"]), description: z.string(), nodes: z.array(z.string()), channel: z.string(), recommended: z.boolean() })
export type Template = z.infer<typeof Template>

// ---- Runs ----

export const RunStatus = z.enum(["running", "advancing", "waiting", "handed_over", "completed", "stopped", "failed", "blocked"])
export type RunStatus = z.infer<typeof RunStatus>
export const RUN_STATUS_LABELS: Record<RunStatus, string> = {
  running: "Running",
  advancing: "Running",
  waiting: "Waiting",
  handed_over: "Handed over",
  completed: "Completed",
  stopped: "Stopped",
  failed: "Failed",
  blocked: "Blocked",
}

export const RunSummary = z.object({
  id: Id,
  automationId: Id,
  automationName: z.string(),
  versionNumber: z.number().int(),
  contactId: Id,
  contactName: z.string(),
  handle: z.string().nullable(),
  conversationId: Id.nullable(),
  status: RunStatus,
  /** Delivery uncertainty on a send step, surfaced separately from run status. */
  needsReview: z.boolean(),
  lastStepTitle: z.string().nullable(),
  outcomeReason: z.string().nullable(),
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  durationSeconds: z.number().nullable(),
})
export type RunSummary = z.infer<typeof RunSummary>

export const RunStep = z.object({
  nodeId: z.string(),
  executionIndex: z.number().int(),
  title: z.string(),
  kind: NodeKind,
  outcome: z.string(),
  detail: z.string(),
  occurredAt: z.iso.datetime(),
  intent: z.object({ id: Id, submissionStatus: z.string(), providerMessageId: z.string().nullable(), attempts: z.number().int(), lastAttemptAt: z.iso.datetime().nullable(), reason: z.string().nullable() }).nullable(),
})
export const RunDetail = z.object({
  run: RunSummary,
  steps: z.array(RunStep),
  cursor: z.object({ nodeId: z.string(), title: z.string(), executionIndex: z.number().int() }),
  waitUntil: z.iso.datetime().nullable(),
  eventLog: z.array(z.object({ type: z.string(), createdAt: z.iso.datetime(), consumedAt: z.iso.datetime().nullable() })),
  canRetry: z.boolean(),
  retryBlockedReason: z.string().nullable(),
})
export type RunDetail = z.infer<typeof RunDetail>

export const RunsQuery = z.object({ status: RunStatus.optional(), q: z.string().trim().max(120).optional(), limit: z.coerce.number().int().min(1).max(200).default(50) })
export type RunsQuery = z.infer<typeof RunsQuery>
export const RunsPage = z.object({
  runs: z.array(RunSummary),
  metrics: z.object({ started: z.number().int(), completed: z.number().int(), handedOver: z.number().int(), needsReview: z.number().int() }),
  skipped: z.array(z.object({ reason: z.string(), label: z.string(), count: z.number().int(), detail: z.string() })),
})
export type RunsPage = z.infer<typeof RunsPage>

export const EnrolInput = z.object({ automationId: Id, confirm: z.literal(true) })

/**
 * Top-to-bottom layout for a flow, the way a reader follows it: the trigger first, each step below the
 * one that leads to it, branches side by side with the "yes" path on the left. Positions are a starting
 * point; the builder lets people drag steps anywhere afterwards.
 */
export function layoutVertical(definition: FlowDefinition, options: { width?: number } = {}): FlowDefinition {
  const width = options.width ?? 826
  const nodeWidth = 220
  const nodeHeight = 96
  const gapX = 48
  const gapY = 72
  const triggers = definition.nodes.filter((n) => n.config.kind === "trigger")
  const roots = triggers.length ? triggers : definition.nodes.slice(0, 1)
  if (!roots.length) return definition
  const level = new Map<string, number>(roots.map((n) => [n.id, 0]))
  const queue = roots.map((n) => n.id)
  while (queue.length) {
    const id = queue.shift()!
    for (const edge of definition.edges) {
      if (edge.from !== id || level.has(edge.to)) continue
      level.set(edge.to, level.get(id)! + 1)
      queue.push(edge.to)
    }
  }
  const deepest = Math.max(0, ...level.values())
  for (const n of definition.nodes) if (!level.has(n.id)) level.set(n.id, deepest + 1)
  const rank = (id: string) => {
    const incoming = definition.edges.find((e) => e.to === id)
    return incoming?.label === "no_match" || incoming?.label === "timeout" ? 1 : 0
  }
  const rows = new Map<number, string[]>()
  for (const n of definition.nodes) {
    const l = level.get(n.id)!
    rows.set(l, [...(rows.get(l) ?? []), n.id])
  }
  for (const [l, ids] of rows) rows.set(l, [...ids].sort((a, b) => rank(a) - rank(b)))
  const nodes = definition.nodes.map((n) => {
    const l = level.get(n.id)!
    const ids = rows.get(l)!
    const index = ids.indexOf(n.id)
    const rowWidth = ids.length * nodeWidth + (ids.length - 1) * gapX
    const x = Math.round(Math.max(16, width / 2 - rowWidth / 2) + index * (nodeWidth + gapX))
    const y = 48 + l * (nodeHeight + gapY)
    return { ...n, x, y }
  })
  return { ...definition, nodes }
}
